/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { type CSSProperties } from 'react';

interface ChipOption {
  value: string;
  label: string;
}

interface ChipSelectSingleProps {
  label: string;
  options: ChipOption[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}

interface ChipSelectMultiProps {
  label: string;
  options: ChipOption[];
  value: string[];
  onChange: (value: string[]) => void;
  required?: boolean;
  /** Optional class for the chips wrap (e.g. max-height + scroll). */
  optionsClassName?: string;
  /** Optional test id for the chips wrap. */
  optionsTestId?: string;
}

const labelStyle: CSSProperties = {
  fontSize: 'var(--text-meta)',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 6,
  display: 'block',
  fontFamily: 'var(--font-family)',
};

const chipBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-tertiary)',
  color: 'var(--text-secondary)',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'var(--font-family)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  userSelect: 'none' as const,
};

const chipSelected: CSSProperties = {
  background: 'var(--selection-selected)',
  borderColor: 'var(--seed-base)',
  color: 'var(--seed-base)',
  fontWeight: 600,
};

export const ChipSelectSingle: React.FC<ChipSelectSingleProps> = ({
  label,
  options,
  value,
  onChange,
  required,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column' }}>
    {label && (
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--status-error)', marginLeft: 2 }}>*</span>}
      </label>
    )}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{ ...chipBase, ...(value === opt.value ? chipSelected : {}) }}
          data-testid={`chip-${opt.value}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

export const ChipSelectMulti: React.FC<ChipSelectMultiProps> = ({
  label,
  options,
  value,
  onChange,
  required,
  optionsClassName,
  optionsTestId,
}) => {
  const selected = value ?? [];
  const toggle = (optValue: string) => {
    if (selected.includes(optValue)) {
      onChange(selected.filter(v => v !== optValue));
    } else {
      onChange([...selected, optValue]);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--status-error)', marginLeft: 2 }}>*</span>}
      </label>
      <div
        data-testid={optionsTestId}
        className={optionsClassName}
        style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
      >
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            style={{ ...chipBase, ...(selected.includes(opt.value) ? chipSelected : {}) }}
            data-testid={`chip-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};
